import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  Search,
  Filter,
  Users,
  Loader2,
  Eye,
  Send,
  Download,
  Info,
  Shield,
  Calendar,
  MapPin,
  Monitor,
  User,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Profile {
  id: string;
  full_name: string;
  employee_id: string;
}

interface PayrollDocument {
  id: string;
  employee_id: string;
  employee_name: string;
  period: string;
  title: string;
  file_url: string;
  file_name: string;
  file_size: number;
  status: "pending" | "signed" | "rejected";
  signed_at: string | null;
  signature_url: string | null;
  signed_from_ip: string | null;
  signed_user_agent: string | null;
  rejection_reason: string | null;
  uploaded_by_name: string | null;
  notes: string | null;
  created_at: string;
  // Expanded
  employee?: {
    id: string;
    full_name: string;
    employee_id: string;
  };
}

export default function PayrollAdmin() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<PayrollDocument | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);

  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [period, setPeriod] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Fetch employees
  const { data: employees } = useQuery({
    queryKey: ["employees-list"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id")
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return data as Profile[];
    },
  });

  // Fetch all payroll documents with employee details
  const { data: documents, isLoading } = useQuery({
    queryKey: ["payroll-documents-admin"],
    queryFn: async () => {
      const { data, error } = await api
        .from("payroll_documents")
        .select("*, employee:profiles!payroll_documents_employee_id_fkey(id, full_name, employee_id)")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as PayrollDocument[];
    },
    refetchInterval: 10000,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file || !selectedEmployee || !period || !title) {
        throw new Error("Todos los campos son requeridos");
      }

      setUploading(true);

      // Upload file
      const formData = new FormData();
      formData.append("folder", "payroll");
      formData.append("file", file);

      const uploadResponse = await fetch("/upload", {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || "Error al subir el archivo");
      }

      const uploadResult = await uploadResponse.json();
      const fileUrl = uploadResult.url;

      const employee = employees?.find(e => e.id === selectedEmployee);

      // Create payroll document
      const { data: newDoc, error } = await api
        .from("payroll_documents")
        .insert([{
          employee_id: selectedEmployee,
          employee_name: employee?.full_name || "Desconocido",
          period,
          title,
          file_url: fileUrl,
          file_name: file.name,
          file_size: file.size,
          status: "pending",
          uploaded_by: profile?.id,
          uploaded_by_name: profile?.full_name,
          notes,
        }])
        .select()
        .single();

      if (error) throw error;

      // Create notification for employee
      await api.from("user_notifications").insert([{
        user_id: selectedEmployee,
        title: "Nueva nómina disponible",
        message: `Tu nómina de ${period} está lista para revisión y firma.`,
        type: "info",
        category: "payroll",
        reference_type: "payroll",
        reference_id: newDoc.id,
        action_url: "/payroll",
      }]);

      return newDoc;
    },
    onSuccess: () => {
      toast({
        title: "Nómina subida",
        description: "El documento se ha subido y el empleado ha sido notificado",
      });
      queryClient.invalidateQueries({ queryKey: ["payroll-documents-admin"] });
      resetForm();
      setDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (docId: string) => {
      const { error } = await api
        .from("payroll_documents")
        .update({ is_active: false })
        .eq("id", docId);
      if (error) throw error;

      // Delete related notification
      await api
        .from("user_notifications")
        .delete()
        .eq("reference_type", "payroll")
        .eq("reference_id", docId);
    },
    onSuccess: () => {
      toast({ title: "Documento eliminado" });
      queryClient.invalidateQueries({ queryKey: ["payroll-documents-admin"] });
      setDeleteDialogOpen(false);
      setSelectedDoc(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setSelectedEmployee("");
    setPeriod("");
    setTitle("");
    setNotes("");
    setFile(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Firmado</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      default:
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    }
  };

  const handlePreview = (doc: PayrollDocument) => {
    setPreviewError(false);
    setPreviewUrl(doc.file_url);
  };

  const handleDownload = async (doc: PayrollDocument) => {
    try {
      const response = await fetch(doc.file_url);
      if (!response.ok) throw new Error("Archivo no encontrado");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Error al descargar",
        description: "El archivo no está disponible. Es posible que haya sido eliminado.",
        variant: "destructive",
      });
    }
  };

  const openDetailDialog = (doc: PayrollDocument) => {
    setSelectedDoc(doc);
    setDetailDialogOpen(true);
  };

  const filteredDocuments = documents?.filter(doc => {
    const matchesSearch = !searchTerm ||
      doc.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || doc.status === statusFilter;
    const matchesPeriod = !periodFilter || doc.period === periodFilter;
    return matchesSearch && matchesStatus && matchesPeriod;
  });

  const periods = [...new Set(documents?.map(d => d.period) || [])].sort().reverse();

  const stats = {
    total: documents?.length || 0,
    pending: documents?.filter(d => d.status === "pending").length || 0,
    signed: documents?.filter(d => d.status === "signed").length || 0,
    rejected: documents?.filter(d => d.status === "rejected").length || 0,
  };

  // Generate period suggestions
  const generatePeriodSuggestions = () => {
    const now = new Date();
    const suggestions = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                          "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
      suggestions.push(`${monthNames[date.getMonth()]} ${date.getFullYear()}`);
    }
    return suggestions;
  };

  const formatUserAgent = (ua: string | null) => {
    if (!ua) return "No disponible";
    // Simplify user agent string
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return ua.substring(0, 50) + "...";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Gestión de Nóminas</h1>
            <p className="text-muted-foreground mt-1">
              Sube y gestiona los documentos de nómina de los empleados
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Subir Nómina
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.signed}</p>
                  <p className="text-sm text-muted-foreground">Firmadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                  <p className="text-sm text-muted-foreground">Rechazadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por empleado o título..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="signed">Firmadas</SelectItem>
              <SelectItem value="rejected">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={periodFilter || "all"} onValueChange={(v) => setPeriodFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los períodos</SelectItem>
              {periods.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredDocuments && filteredDocuments.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleado</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha subida</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow
                      key={doc.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        doc.status === "signed" && "bg-green-50/50 dark:bg-green-950/10"
                      )}
                      onClick={() => openDetailDialog(doc)}
                    >
                      <TableCell>
                        <div>
                          <span className="font-medium">{doc.employee_name}</span>
                          {doc.employee?.employee_id && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({doc.employee.employee_id})
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{doc.period}</TableCell>
                      <TableCell>{doc.title}</TableCell>
                      <TableCell>
                        {getStatusBadge(doc.status)}
                        {doc.status === "signed" && doc.signed_at && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(doc.signed_at).toLocaleDateString("es-ES")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(doc.created_at).toLocaleDateString("es-ES")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreview(doc)}
                            title="Vista previa"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(doc)}
                            title="Descargar"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDetailDialog(doc)}
                            title="Ver detalles"
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setDeleteDialogOpen(true);
                            }}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No hay documentos de nómina</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Subir Nómina</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Empleado *</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar empleado" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees?.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.full_name} {emp.employee_id && `(${emp.employee_id})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Período *</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar período" />
                  </SelectTrigger>
                  <SelectContent>
                    {generatePeriodSuggestions().map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Nómina Marzo 2026"
                />
              </div>

              <div className="space-y-2">
                <Label>Documento PDF *</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                {file && (
                  <p className="text-sm text-muted-foreground">
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => uploadMutation.mutate()}
                disabled={uploading || !file || !selectedEmployee || !period || !title}
              >
                {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Send className="w-4 h-4 mr-2" />
                Subir y Notificar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog - Shows signature info */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Detalle de Nómina</DialogTitle>
            </DialogHeader>

            {selectedDoc && (
              <div className="space-y-4">
                {/* Document Info */}
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{selectedDoc.title}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Empleado:</span>
                      <p className="font-medium">{selectedDoc.employee_name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Período:</span>
                      <p className="font-medium">{selectedDoc.period}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Estado:</span>
                      <div className="mt-1">{getStatusBadge(selectedDoc.status)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Subido por:</span>
                      <p className="font-medium">{selectedDoc.uploaded_by_name || "Sistema"}</p>
                    </div>
                  </div>
                </div>

                {/* Signature Info - Only for signed documents */}
                {selectedDoc.status === "signed" && (
                  <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 p-4 space-y-3">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <Shield className="w-5 h-5" />
                      <span className="font-semibold">Documento Firmado Electrónicamente</span>
                    </div>

                    <Separator className="bg-green-200 dark:bg-green-800" />

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Fecha y hora:</span>
                        <span className="font-medium">
                          {selectedDoc.signed_at
                            ? new Date(selectedDoc.signed_at).toLocaleString("es-ES", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })
                            : "No disponible"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">ID Empleado:</span>
                        <span className="font-medium font-mono">
                          {selectedDoc.employee?.employee_id || selectedDoc.employee_id.substring(0, 8)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">IP:</span>
                        <span className="font-medium font-mono">
                          {selectedDoc.signed_from_ip || "No registrada"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Navegador:</span>
                        <span className="font-medium">
                          {formatUserAgent(selectedDoc.signed_user_agent)}
                        </span>
                      </div>
                    </div>

                    {/* Signature preview if available */}
                    {selectedDoc.signature_url && (
                      <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                        <p className="text-xs text-muted-foreground mb-2">Firma digital:</p>
                        <div className="bg-white rounded border p-2 inline-block">
                          <img
                            src={selectedDoc.signature_url}
                            alt="Firma"
                            className="max-h-16"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Rejection Info */}
                {selectedDoc.status === "rejected" && selectedDoc.rejection_reason && (
                  <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 p-4">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
                      <XCircle className="w-5 h-5" />
                      <span className="font-semibold">Documento Rechazado</span>
                    </div>
                    <p className="text-sm">{selectedDoc.rejection_reason}</p>
                  </div>
                )}

                {/* Notes */}
                {selectedDoc.notes && (
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground mb-1">Notas:</p>
                    <p className="text-sm">{selectedDoc.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => handlePreview(selectedDoc)} className="flex-1">
                    <Eye className="w-4 h-4 mr-2" />
                    Ver documento
                  </Button>
                  <Button variant="outline" onClick={() => handleDownload(selectedDoc)} className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de eliminar este documento de nómina? Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => selectedDoc && deleteMutation.mutate(selectedDoc.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Preview Dialog */}
        <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Vista previa del documento</DialogTitle>
            </DialogHeader>
            {previewUrl && !previewError ? (
              <iframe
                src={previewUrl}
                className="w-full h-[70vh] border rounded"
                title="Document Preview"
                onError={() => setPreviewError(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[40vh] text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mb-4 text-yellow-500" />
                <p className="font-medium">No se pudo cargar el documento</p>
                <p className="text-sm mt-1">El archivo puede haber sido eliminado o movido.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setPreviewUrl(null)}
                >
                  Cerrar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
